/**
 * useSignInWithBiometric — cold-open passwordless sign-in.
 *
 * Three-step flow:
 *   1. Run the WebAuthn assertion ceremony (getSigninAssertion in
 *      lib/webauthn/ceremony.ts) — fetches a server challenge with
 *      purpose='signin' (no JWT required), then calls
 *      navigator.credentials.get with an empty allowCredentials list
 *      so the OS presents discoverable credentials via FaceID /
 *      fingerprint / Windows Hello.
 *   2. POST the assertion to the webauthn-signin Edge Function. The
 *      server resolves the user from the credential_id, runs the same
 *      ceremony + signature checks as webauthn-verify, and on success
 *      returns an OTP minted via auth.admin.generateLink magiclink.
 *   3. Redeem the OTP via supabase.auth.verifyOtp to obtain a real
 *      Supabase session.
 *
 * Returned errors are structured AppError. The user-cancelled-prompt
 * branch is mapped distinctly (BIOMETRIC_CANCELLED) so the UI doesn't
 * show a scary "verification failed" message when the user simply
 * dismissed the OS prompt.
 */

import { useEffect, useState } from 'react'
import { supabase }            from '@/lib/supabase'
import { AppError }            from '@/lib/errors/AppError'
import { getSigninAssertion }  from '@/lib/webauthn/ceremony'
import { isPlatformAuthenticatorAvailable } from '@/lib/webauthn/support'

export interface BiometricSigninResult {
  /** True if the device has a platform authenticator (FaceID / fingerprint / Windows Hello). */
  supported:  boolean | null
  signInWithBiometric: () => Promise<void>
  isPending:  boolean
  error:      AppError | null
}

export function useSignInWithBiometric(): BiometricSigninResult {
  const [supported, setSupported] = useState<boolean | null>(null)
  const [isPending, setPending]   = useState(false)
  const [error,     setError]     = useState<AppError | null>(null)

  useEffect(() => {
    let cancelled = false
    isPlatformAuthenticatorAvailable().then((ok) => {
      if (!cancelled) setSupported(ok)
    })
    return () => { cancelled = true }
  }, [])

  const signInWithBiometric = async (): Promise<void> => {
    setPending(true)
    setError(null)

    let assertion
    try {
      assertion = await getSigninAssertion({ supabase })
    } catch (e) {
      const isUserCancel =
        e instanceof DOMException &&
        (e.name === 'NotAllowedError' || e.name === 'AbortError')
      const appErr = new AppError({
        code:        isUserCancel ? 'BIOMETRIC_CANCELLED' : 'BIOMETRIC_CEREMONY_FAILED',
        message:     e instanceof Error ? e.message : 'WebAuthn ceremony failed',
        userMessage: isUserCancel
          ? 'Biometric sign-in was cancelled.'
          : 'Could not start the biometric prompt. Try again or sign in with your PIN.',
        status:      isUserCancel ? 400 : 500,
      })
      setPending(false)
      // Cancellation is not really an error — leave error null so the UI
      // does not show a red panel for a user-driven dismiss.
      if (!isUserCancel) setError(appErr)
      throw appErr
    }

    let invokeData: unknown
    let invokeError: { message: string; status?: number; context?: Response } | null = null
    try {
      const result = await supabase.functions.invoke('webauthn-signin', { body: assertion })
      invokeData  = result.data
      invokeError = result.error as typeof invokeError
    } catch (e) {
      invokeError = { message: e instanceof Error ? e.message : 'Network error' }
    }

    if (invokeError) {
      const status = invokeError.status ?? 500
      let serverCode    = 'WEBAUTHN_SIGNIN_FAILED'
      let serverDetail  = invokeError.message
      if (invokeError.context) {
        try {
          const body = await invokeError.context.clone().json() as { error?: string; detail?: string }
          if (typeof body.error === 'string')   serverCode   = body.error
          if (typeof body.detail === 'string')  serverDetail = body.detail
        } catch { /* keep defaults */ }
      }
      const appErr = new AppError({
        code:        serverCode,
        message:     serverDetail,
        userMessage: friendlyMessage(serverCode, serverDetail),
        status,
      })
      setPending(false)
      setError(appErr)
      throw appErr
    }

    const data = invokeData as { email?: unknown; otp?: unknown } | null
    if (!data || typeof data.email !== 'string' || typeof data.otp !== 'string') {
      const appErr = new AppError({
        code:        'WEBAUTHN_SIGNIN_BAD_RESPONSE',
        message:     'webauthn-signin returned an unexpected response shape',
        userMessage: 'Sign-in failed. Please try again.',
        status:      500,
      })
      setPending(false)
      setError(appErr)
      throw appErr
    }

    const { error: otpError } = await supabase.auth.verifyOtp({
      email: data.email,
      token: data.otp,
      type:  'email',
    })

    setPending(false)

    if (otpError) {
      const appErr = new AppError({
        code:        'OTP_REDEEM_FAILED',
        message:     otpError.message,
        userMessage: 'Sign-in failed at the session-issuance step. Please try again.',
        status:      500,
        cause:       otpError,
      })
      setError(appErr)
      throw appErr
    }
  }

  return { supported, signInWithBiometric, isPending, error }
}

function friendlyMessage(code: string, detail: string): string {
  switch (code) {
    case 'CHALLENGE_INVALID':
    case 'CHALLENGE_EXPIRED_OR_USED':
    case 'CEREMONY_MISMATCH':
    case 'ORIGIN_REJECTED':
    case 'RP_ID_MISMATCH':
    case 'USER_NOT_PRESENT':
    case 'INVALID_PAYLOAD':
      return 'Biometric sign-in failed. Try again or sign in with your PIN.'
    case 'CREDENTIAL_NOT_FOUND':
      return 'No biometric credential is registered for this device. Sign in with your PIN, then enable biometric in settings.'
    case 'REPLAY_DETECTED':
    case 'SIGNATURE_INVALID':
      return 'Biometric verification failed. Sign in with your PIN.'
    case 'RATE_LIMITED':
      return 'Too many sign-in attempts. Please wait a moment and try again.'
    default:
      return detail || 'Sign-in failed. Please try again.'
  }
}
